import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';

import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription, filter } from 'rxjs';

import { GolfEvent, GolfTournament } from 'src/app/_interfaces/golf/golf-tournament';
import { GolfRound, GolfScoreCardModel } from 'src/app/_interfaces/golf/golf-score-card';
import { AnalyticsService, AppStringsService } from 'src/app/_services';
import { ScheduledEntryStateService } from 'src/app/tournament-section/tournament/scheduled-entry-state.service';

import { formatLocalShortTimeFromCET } from 'src/app/_functions/datetimes';
import { SponsorBannerComponent } from 'src/app/_modules/sponsor-banner/sponsor-banner.component';
import { ImageComponent } from 'src/app/_modules/image/image.component';
import { LoadingIndicatorComponent } from 'src/app/_modules/loading-indicator/loading-indicator.component';
import { GolfPlayer, Player } from 'src/app/_interfaces';
import { PlayerDialogComponent } from 'src/app/_dialogs/player-dialog/player-dialog.component';
import { MatDialog } from '@angular/material/dialog';

type RoundKey = `round_${1 | 2 | 3 | 4}`;

interface TeeTimeRow {
    playerId: string;
    fullName: string;
    player: GolfPlayer;
    teeTimeRaw: string;
    teeTimeDisplay: string;
    startingHole: number | null;
    enetPlayerNumber: number;
    courseAbbreviation?: string | null;
}

@Component({
    selector: 'app-golf-tee-times',
    standalone: true,
    imports: [
        MatTableModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatIconModule,
        ReactiveFormsModule,
        SponsorBannerComponent,
        ImageComponent,
        LoadingIndicatorComponent
    ],
    templateUrl: './golf-tee-times.component.html',
    styleUrls: ['./golf-tee-times.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GolfTeeTimesComponent implements OnInit, OnDestroy {
    golfTour: GolfTournament | null = null;
    isLoading = true;
    hasLoaded = false;

    roundPicker = new FormControl<string>('');
    search = new FormControl<string>('');

    availableRounds: Array<{ labelKey: string; value: RoundKey }> = [];
    rowsAll: TeeTimeRow[] = [];
    rowsFiltered: TeeTimeRow[] = [];

    displayedColumns: string[] = ['player', 'time', 'tee'];

    private subs = new Subscription();

    constructor(
        private entryState: ScheduledEntryStateService,
        public appStrings: AppStringsService,
        private analytics: AnalyticsService,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog
    ) { }

    private get hasMultipleCourses(): boolean {
        return (this.golfTour?.courses?.length ?? 0) > 1;
    }

    private resolveCourseAbbreviationFromRound(rd: GolfRound): string | null {
        if (!this.hasMultipleCourses) return null;

        const courseId = rd?.course_id ?? null;

        if (!courseId) return null;

        const course = this.golfTour?.courses?.find(c => c?.id === courseId);
        return this.formatCourseAbbreviation(course?.course_abbreviation ?? null);
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        this.subs.add(this.roundPicker.valueChanges.subscribe(() => this.applyFilters()));
        this.subs.add(this.search.valueChanges.subscribe(() => this.applyFilters()));

        this.subs.add(
            this.entryState.tournament
                .pipe(filter((t): t is GolfTournament => !!t?.id))
                .subscribe((t) => {
                    this.golfTour = t;

                    const event = this.getStrokePlayEvent(t);
                    if (!event) {
                        this.availableRounds = [];
                        this.rowsAll = [];
                        this.rowsFiltered = [];
                        this.isLoading = false;
                        this.hasLoaded = true;
                        this.cdr.markForCheck();
                        return;
                    }

                    this.availableRounds = this.computeAvailableRounds(event);

                    const preferred = this.resolvePreferredDefaultRound(event);

                    const current = this.roundPicker.value as RoundKey | null;
                    const currentIsValid = !!current && this.availableRounds.some(r => r.value === current);

                    if (!currentIsValid) {
                        const safeDefault =
                            this.availableRounds.some(r => r.value === preferred)
                                ? preferred
                                : (this.availableRounds[0]?.value ?? 'round_1');

                        this.roundPicker.setValue(safeDefault, { emitEvent: false });
                    }

                    this.rowsAll = this.buildRowsForRound(event, this.roundPicker.value as RoundKey);
                    this.applyFilters();

                    this.isLoading = false;
                    this.hasLoaded = true;

                    this.cdr.markForCheck();
                })
        );

        await this.analytics.postEvent('screen_view', {
            screen_name: 'Golf Tee Times',
            parameters: {},
        });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    get showEmpty(): boolean {
        return this.hasLoaded && !this.isLoading && this.rowsFiltered.length === 0;
    }

    get showTable(): boolean {
        return this.hasLoaded && !this.isLoading && this.rowsFiltered.length > 0;
    }

    get searchPlaceholder(): string {
        return this.appStrings.getAppString('golf.leaderboard.search.placeholder');
    }

    clearSearch(ev: MouseEvent): void {
        ev.stopPropagation();
        this.search.setValue('');
    }

    private getStrokePlayEvent(t: GolfTournament): GolfEvent | null {
        // Only show tab for stroke_play + hole_by_hole_scoring === true
        if (t?.hole_by_hole_scoring !== true) return null;

        return t?.events?.find(e => e.type === 'stroke_play') ?? null;
    }

    private computeAvailableRounds(event: GolfEvent): Array<{ labelKey: string; value: RoundKey }> {
        const totalRounds = (event.number_of_rounds ?? 4);
        const options: Array<{ labelKey: string; value: RoundKey }> = [];

        for (let r = 1 as 1 | 2 | 3 | 4; r <= Math.min(totalRounds, 4); r = (r + 1)) {
            const key = `round_${r}` as RoundKey;

            const hasAny = (event.scores as GolfScoreCardModel[]).some((sc) => {
                const rd = sc?.[key];
                return !!rd?.tee_time;
            });

            if (hasAny) {
                const labelKey =
                    r === 1 ? 'golf.rounds.one'
                        : r === 2 ? 'golf.rounds.two'
                            : r === 3 ? 'golf.rounds.three'
                                : 'golf.rounds.four';

                options.push({ labelKey, value: key });
            }
        }

        // no tee times anywhere yet → show Round 1 so picker isn't blank
        if (options.length === 0) {
            options.push({ labelKey: 'golf.rounds.one', value: 'round_1' });
        }

        return options;
    }

    private buildRowsForRound(event: GolfEvent, roundKey: RoundKey): TeeTimeRow[] {
        const scores = (event.scores ?? []) as GolfScoreCardModel[];
        const rows: TeeTimeRow[] = [];

        scores.forEach((sc: any) => {
            const rd = sc?.[roundKey];
            const tee = rd?.tee_time ?? null;
            const startingHole: number | null = rd?.starting_hole ?? null;

            // Only include players that have tee times for selected round
            if (!tee) return;

            const first = sc?.player?.first_name ?? '';
            const last = sc?.player?.last_name ?? '';
            const fullName = `${first} ${last}`.trim();

            // Display local time, append '*' for back nine (10-18)
            let display = formatLocalShortTimeFromCET(tee);
            if (startingHole !== null && startingHole >= 10 && startingHole <= 18) {
                display = `${display}*`;
            }

            const enetRaw = sc?.enet_player_number;
            const enetPlayerNumber =
                typeof enetRaw === 'number' ? enetRaw
                    : (typeof enetRaw === 'string' && /^\d+$/.test(enetRaw)) ? Number(enetRaw)
                        : Number.MAX_SAFE_INTEGER;

            const courseAbbreviation = this.resolveCourseAbbreviationFromRound(rd);

            rows.push({
                playerId: sc?.player?.id,
                fullName,
                player: sc?.player,
                teeTimeRaw: tee,
                teeTimeDisplay: display,
                startingHole,
                enetPlayerNumber,
                courseAbbreviation,
            });
        });

        // Sorting: time -> starting tee -> enet_player_number
        rows.sort((a, b) => {
            const tCmp = a.teeTimeRaw.localeCompare(b.teeTimeRaw);
            if (tCmp !== 0) return tCmp;

            const aHole = a.startingHole ?? Number.MAX_SAFE_INTEGER;
            const bHole = b.startingHole ?? Number.MAX_SAFE_INTEGER;
            const hCmp = aHole - bHole;
            if (hCmp !== 0) return hCmp;

            return a.enetPlayerNumber - b.enetPlayerNumber;
        });

        return rows;
    }

    private applyFilters(): void {
        if (this.golfTour) {
            const event = this.getStrokePlayEvent(this.golfTour);
            if (!event) {
                this.rowsFiltered = [];
                this.cdr.markForCheck();
                return;
            }

            const roundKey = (this.roundPicker.value as RoundKey) || ('round_1' as RoundKey);
            const rows = this.buildRowsForRound(event, roundKey);

            const term = (this.search.value ?? '').toString().trim().toLowerCase();
            let filtered = rows;

            if (term) {
                filtered = rows.filter(r => r.fullName.toLowerCase().includes(term));
            }

            this.rowsAll = rows;
            this.rowsFiltered = filtered;
            this.cdr.markForCheck();
        }
    }

    openPlayer(golf_player: GolfPlayer | Player): void {
        if (!golf_player) return;

        this.dialog.open(PlayerDialogComponent, {
            data: golf_player,
            width: '100%',
            height: '90vh',
            maxWidth: '750px',
            panelClass: 'full-screen-dialog'
        });
    }

    private formatCourseAbbreviation(abbr?: string | null): string | null {
        const normalized = abbr?.trim().slice(0, 2).toUpperCase();
        return normalized ? normalized : null;
    }

    private mostRecentRoundIndex(roundKey?: string | null): number | null {
        if (!roundKey || roundKey === 'round_final') return null;
        const map: Record<string, number> = { round_1: 1, round_2: 2, round_3: 3, round_4: 4 };
        return map[roundKey] ?? null;
    }

    private resolvePreferredDefaultRound(event: GolfEvent): RoundKey {
        // 1) Finished event -> Round 1
        if (event.status === 'finished') return 'round_1';

        // 2) Live event -> use most_recently_scored_round (matches leaderboard highlight)
        const idx = this.mostRecentRoundIndex(event.most_recently_scored_round);
        if (idx) return `round_${idx}` as RoundKey;

        // 3) Upcoming / no scoring yet -> Round 1
        return 'round_1';
    }
}
